import type { AuthLoginResponse } from '@/api/gen-new/model';

import {
	authLogin,
	authLoginGoogle,
	authLoginTwoFactor,
	authLogout,
	authRefresh,
	getAuthMeQueryKey,
} from '@/api/gen-new/auth/auth';
import { getGaClientId } from '@/analytics/gtag';
import { getStoredAttribution } from '@/analytics/attribution';
import { queryClient } from '@/queryClient';
import { jwtDecode } from 'jwt-decode';

import type { UserAuthJwtPayload } from './AuthProvider';
import type { AuthSessionData } from './AuthSession';

import { getUserLocale } from '@/utils/getUserLocale';
import { destroySession, getSession, saveSession } from './AuthSession';
import { noteNonBiometricSignIn } from './biometric/biometricEnrollment';
import { AuthenticationError, InvitationError, TokenRefreshError, TwoFactorCodeError } from './Errors';

/**
 * Module-scoped variable to store the promise of an ongoing token refresh operation.
 * If this is not null, a refresh is in progress, and subsequent calls should await this promise.
 */
let refreshPromise: Promise<AuthSessionData> | null = null;

export type EmailSignInResult =
	| { status: 'signed-in'; session: AuthSessionData }
	/** The password was right and the account has two-factor authentication. */
	| { status: 'two-factor-required'; twoFactorToken: string };

async function saveEmailSession(
	tokens: AuthLoginResponse,
	email: string,
	{ offerBiometrics }: { offerBiometrics: boolean },
): Promise<AuthSessionData> {
	const { accessToken, expiresIn, refreshToken, refreshTokenExpiresIn } = tokens;
	const tokenData = jwtDecode<UserAuthJwtPayload>(accessToken);

	const session: AuthSessionData = {
		userId: Number(tokenData.sub),
		userGroupId: tokenData.userGroupId,
		preferredCurrency: tokenData.preferredCurrency,
		preferredLang: tokenData.preferredLang,
		isLoggedIn: true,
		accessToken,
		expiresIn,
		refreshToken,
		refreshTokenExpiresIn,
		loginMethod: 'email',
		googleId: undefined,
		email,
	};

	saveSession(session);
	noteNonBiometricSignIn(Number(tokenData.sub), { offerBiometrics });

	// Invalidate authme queries to ensure fresh user data is fetched
	await queryClient.invalidateQueries({ queryKey: getAuthMeQueryKey() });

	return session;
}

/**
 * Authenticates user with email and password
 * @param email User's email address
 * @param password User's password
 * @returns Promise resolving to the session, or to the token for the two-factor code step
 * @throws AuthenticationError for authentication failures
 */
export async function signInWithEmail(
	email: string,
	password: string,
	invitationCode?: string,
): Promise<EmailSignInResult> {
	try {
		const { session, twoFactorToken } = await authLogin({
			email,
			password,
			invitationCode,
		});

		if (twoFactorToken) {
			return { status: 'two-factor-required', twoFactorToken };
		}
		if (!session) {
			throw new AuthenticationError('No session in login response');
		}

		return { status: 'signed-in', session: await saveEmailSession(session, email, { offerBiometrics: true }) };
	} catch (error: unknown) {
		// Check if it's an AxiosError and determine the appropriate error type
		if (error && typeof error === 'object' && 'response' in error) {
			const axiosError = error as { response?: { status: number; data?: { message?: string } } };

			if (axiosError.response?.status === 422) {
				// 422 = Invitation-related errors (invalid code, expired, etc.)
				// Don't sign out for invitation errors - credentials might be valid
				throw new InvitationError('Invitation error', error instanceof Error ? error : new Error(String(error)));
			}

			if (axiosError.response?.status === 401) {
				// Check if it's specifically a user not verified error
				const errorMessage = axiosError.response.data?.message;
				if (errorMessage === 'auth.authenticate.user.not.verified') {
					// Don't sign out for verification errors - credentials are valid
					throw new AuthenticationError(
						'User not verified',
						error instanceof Error ? error : new Error(String(error)),
						'USER_NOT_VERIFIED',
					);
				}
			}
		}

		// For authentication errors, only clear session if there's no invitation code
		// When there's an invitation code, preserve the session to keep the invitation context
		if (!invitationCode) {
			signOut();
		}

		// Default to authentication error for 401 and other errors
		throw new AuthenticationError(
			'Email authentication failed',
			error instanceof Error ? error : new Error(String(error)),
		);
	}
}

/**
 * Finishes a password sign-in with a code from the authenticator app or a recovery code.
 * The Face ID offer is skipped: turning it on needs a new code, and the one just typed is used up.
 * @throws TwoFactorCodeError when the code is wrong, codes are blocked, or the token expired
 */
export async function completeTwoFactorSignIn(
	twoFactorToken: string,
	code: string,
	email: string,
): Promise<AuthSessionData> {
	let tokens: AuthLoginResponse;
	try {
		tokens = await authLoginTwoFactor({ twoFactorToken, code });
	} catch (error: unknown) {
		throw TwoFactorCodeError.fromResponse(error);
	}

	return saveEmailSession(tokens, email, { offerBiometrics: false });
}

/**
 * Sign in with Google using an ID token
 * @param idToken - The Google ID token
 * @param invitationCode - Optional invitation code for group invitations
 * @returns Promise resolving to AuthSessionData plus isNewUser if successful
 * @throws AuthenticationError for authentication failures
 */
export async function signInWithGoogle(
	idToken: string,
	invitationCode?: string,
): Promise<AuthSessionData & { isNewUser: boolean }> {
	try {
		// GA4 attribution captured at signup: the client_id ties the eventual server-side purchase
		// back to this visitor, and the stored first-touch UTM/referrer let paid conversions trace
		// to the originating campaign. Best-effort — undefined when GA is blocked/unconfigured or
		// on native. getGaClientId self-caps at ~1s so it never blocks sign-in.
		const gaClientId = await getGaClientId();
		const attribution = getStoredAttribution();

		const { accessToken, expiresIn, refreshToken, refreshTokenExpiresIn, isNewUser } = await authLoginGoogle(
			{
				idToken,
				invitationCode,
				gaClientId,
				...attribution,
			},
			{
				headers: {
					'Accept-Language': getUserLocale(),
				},
			},
		);

		const tokenData = jwtDecode<UserAuthJwtPayload>(accessToken);
		const googleTokenData = jwtDecode<{ sub?: string; email?: string }>(idToken);

		const session: AuthSessionData = {
			userId: Number(tokenData.sub),
			userGroupId: tokenData.userGroupId,
			preferredCurrency: tokenData.preferredCurrency,
			preferredLang: tokenData.preferredLang,
			isLoggedIn: true,
			accessToken,
			expiresIn,
			refreshToken,
			refreshTokenExpiresIn,
			loginMethod: 'google',
			googleId: googleTokenData.sub,
			email: googleTokenData.email,
		};

		saveSession(session);
		// A brand-new account is headed into onboarding; the Face ID offer can wait for a later sign-in.
		noteNonBiometricSignIn(Number(tokenData.sub), { offerBiometrics: !isNewUser });

		// Invalidate authme queries to ensure fresh user data is fetched
		await queryClient.invalidateQueries({ queryKey: getAuthMeQueryKey() });

		// isNewUser is returned to the caller to drive one-time signup funnel events, but it is
		// deliberately NOT persisted in the saved session (it describes this request, not the user).
		return { ...session, isNewUser };
	} catch (error: unknown) {
		// Check if it's an AxiosError and determine the appropriate error type
		if (error && typeof error === 'object' && 'response' in error) {
			const axiosError = error as { response?: { status: number } };

			if (axiosError.response?.status === 422) {
				// 422 = Invitation-related errors (invalid code, expired, etc.)
				// Don't sign out for invitation errors - credentials might be valid
				throw new InvitationError('Invitation error', error instanceof Error ? error : new Error(String(error)));
			}
		}

		// For authentication errors, only clear session if there's no invitation code
		// When there's an invitation code, preserve the session to keep the invitation context
		if (!invitationCode) {
			signOut();
		}

		// Default to authentication error for 401 and other errors
		throw new AuthenticationError(
			'Google authentication failed',
			error instanceof Error ? error : new Error(String(error)),
		);
	}
}

/**
 * Persists a session from a token response that did not come from a login form.
 */
export async function startSessionFromTokens(
	tokens: Pick<AuthSessionData, 'accessToken' | 'expiresIn' | 'refreshToken' | 'refreshTokenExpiresIn'>,
	extras: Pick<AuthSessionData, 'loginMethod' | 'email'>,
): Promise<AuthSessionData> {
	const { accessToken, expiresIn, refreshToken, refreshTokenExpiresIn } = tokens;

	if (!accessToken) {
		throw new AuthenticationError('No access token in response');
	}

	const tokenData = jwtDecode<UserAuthJwtPayload>(accessToken);

	const session: AuthSessionData = {
		userId: Number(tokenData.sub),
		userGroupId: tokenData.userGroupId,
		preferredCurrency: tokenData.preferredCurrency,
		preferredLang: tokenData.preferredLang,
		isLoggedIn: true,
		accessToken,
		expiresIn,
		refreshToken,
		refreshTokenExpiresIn,
		googleId: undefined,
		...extras,
	};

	saveSession(session);

	await queryClient.invalidateQueries({ queryKey: getAuthMeQueryKey() });

	return session;
}

/**
 * Signs out the current user by clearing session and query cache
 */
export function signOut(): void {
	const refreshToken = getSession()?.refreshToken;
	if (refreshToken && isTokenUsable(refreshToken, 0)) {
		void authLogout({ headers: { Authorization: `Bearer ${refreshToken}` } }).catch(() => undefined);
	}

	destroySession();
	queryClient.clear();
	queryClient.removeQueries();
}

/**
 * Refreshes the access token using the current refresh token
 * @param force Whether to force refresh even if current token is still valid
 * @returns Promise resolving to updated session data or null if refresh fails
 * @throws TokenRefreshError for various refresh failure scenarios
 */
export async function refreshAccessToken(force: boolean = false): Promise<AuthSessionData> {
	try {
		const currentSession = getSession();
		if (!currentSession) {
			throw new TokenRefreshError('No session found', 'INVALID_TOKEN', true);
		}

		const { accessToken, refreshToken: currentRefreshToken } = currentSession;
		if (!accessToken || !currentRefreshToken) {
			throw new TokenRefreshError('Missing tokens in session', 'INVALID_TOKEN', true);
		}

		// If not forcing and the current access token is still usable (within buffer), return the current session.
		if (!force && isTokenUsable(accessToken)) {
			return currentSession;
		}

		// Check if refresh token is still valid
		if (!isTokenUsable(currentRefreshToken)) {
			throw new TokenRefreshError('Refresh token is expired', 'EXPIRED_REFRESH_TOKEN', true);
		}

		// If a refresh operation is already in progress, return its promise.
		// This ensures that concurrent calls share the result of the single ongoing refresh.
		if (refreshPromise) {
			return refreshPromise;
		}

		// Start the refresh operation
		refreshPromise = performTokenRefresh(currentRefreshToken, currentSession);

		const result = await refreshPromise;

		// Clear the promise once the refresh operation completes (success or failure)
		refreshPromise = null;

		return result;
	} catch (synchronousError: unknown) {
		// Clear the promise once the refresh operation completes (success or failure)
		refreshPromise = null;

		if (synchronousError instanceof TokenRefreshError) {
			throw synchronousError;
		}

		throw new TokenRefreshError(
			'Unexpected error during token refresh setup',
			'UNKNOWN_ERROR',
			false,
			synchronousError instanceof Error ? synchronousError : new Error(String(synchronousError)),
		);
	}
}

/**
 * Performs the actual token refresh API call
 * @param refreshToken The refresh token to use
 * @param currentSession Current session data
 * @returns Promise resolving to updated session data
 * @throws TokenRefreshError for various API failure scenarios
 */
async function performTokenRefresh(refreshToken: string, currentSession: AuthSessionData): Promise<AuthSessionData> {
	try {
		const response = await authRefresh({
			headers: {
				Authorization: `Bearer ${refreshToken}`,
			},
		});

		const {
			accessToken: newAccessToken,
			expiresIn: newExpiresIn,
			refreshToken: newRefreshToken,
			refreshTokenExpiresIn: newRefreshTokenExpiresIn,
		} = response;

		if (!newAccessToken || !newExpiresIn || !newRefreshToken || !newRefreshTokenExpiresIn) {
			throw new TokenRefreshError('Invalid response from refresh endpoint', 'INVALID_TOKEN', true);
		}

		const tokenData = jwtDecode<UserAuthJwtPayload>(newAccessToken);

		if (!tokenData.sub || !tokenData.userGroupId || !tokenData.preferredCurrency) {
			throw new TokenRefreshError('Invalid token payload from refresh endpoint', 'INVALID_TOKEN', true);
		}

		const parsedUserId = Number.parseInt(tokenData.sub, 10);
		if (Number.isNaN(parsedUserId)) {
			throw new TokenRefreshError('Invalid user ID in token payload', 'INVALID_TOKEN', true);
		}

		const updatedSession: AuthSessionData = {
			...currentSession,
			userId: parsedUserId,
			userGroupId: tokenData.userGroupId,
			preferredCurrency: tokenData.preferredCurrency,
			preferredLang: tokenData.preferredLang,
			isLoggedIn: true,
			accessToken: newAccessToken,
			expiresIn: newExpiresIn,
			refreshToken: newRefreshToken,
			refreshTokenExpiresIn: newRefreshTokenExpiresIn,
		};

		saveSession(updatedSession);

		// Invalidate authme queries to ensure fresh user data is fetched
		await queryClient.invalidateQueries({ queryKey: getAuthMeQueryKey() });

		return updatedSession;
	} catch (error: unknown) {
		const originalError = error instanceof Error ? error : new Error(String(error));

		// Enhanced error classification using HTTP status codes and error types
		// This is more reliable than string matching

		// Check if it's an Axios error with response
		if (error && typeof error === 'object' && 'response' in error && error.response) {
			const status = (error.response as any)?.status;

			// Authentication/Authorization errors - should sign out
			if (status === 401 || status === 403) {
				throw new TokenRefreshError(
					`Authentication failed with status ${status}`,
					'EXPIRED_REFRESH_TOKEN',
					true,
					originalError,
				);
			}

			// Server errors - temporary, don't sign out
			if (status >= 500 && status <= 599) {
				throw new TokenRefreshError(
					`Server error during token refresh (status ${status})`,
					'NETWORK_ERROR',
					false,
					originalError,
				);
			}

			// Client errors other than auth - don't sign out (might be temporary)
			if (status >= 400 && status <= 499) {
				throw new TokenRefreshError(
					`Client error during token refresh (status ${status})`,
					'UNKNOWN_ERROR',
					false,
					originalError,
				);
			}
		}

		// Check for network/connection errors (no response received)
		if (error && typeof error === 'object' && 'code' in error) {
			const errorCode = (error as any).code;

			// Common network error codes
			if (
				errorCode === 'NETWORK_ERROR'
				|| errorCode === 'ECONNREFUSED'
				|| errorCode === 'ETIMEDOUT'
				|| errorCode === 'ENOTFOUND'
				|| errorCode === 'ECONNRESET'
			) {
				throw new TokenRefreshError(
					`Network error during token refresh (${errorCode})`,
					'NETWORK_ERROR',
					false,
					originalError,
				);
			}
		}

		// Check for timeout errors
		if (originalError.message.toLowerCase().includes('timeout')) {
			throw new TokenRefreshError('Request timeout during token refresh', 'NETWORK_ERROR', false, originalError);
		}

		// Unknown error - be conservative and don't automatically sign out
		throw new TokenRefreshError('Unknown error during token refresh', 'UNKNOWN_ERROR', false, originalError);
	}
}

/**
 * Checks if the current user is authenticated
 * @returns true if user has a valid session, false otherwise
 */
export function isAuthenticated(): boolean {
	try {
		const session = getSession();

		if (!session) {
			return false;
		}

		const { accessToken, expiresIn, refreshToken, refreshTokenExpiresIn } = session;
		if (!accessToken || !expiresIn || !refreshToken || !refreshTokenExpiresIn) {
			return false;
		}

		// Check if both tokens are actually usable (not expired)
		// If access token is expired but refresh token is valid, we could still consider
		// the user "authenticated" since we can refresh automatically
		const isRefreshTokenUsable = isTokenUsable(refreshToken);

		// User is considered authenticated if they have a valid refresh token
		// (access token can be refreshed automatically)
		return isRefreshTokenUsable;
	} catch {
		return false;
	}
}

/**
 * Checks if a JWT is currently valid and usable.
 *
 * A token is considered NOT usable if:
 * 1. It cannot be decoded.
 * 2. The 'exp' (expiration time) claim is missing or not a number.
 * 3. The current time (plus an optional buffer) is at or after the 'exp' claim.
 *
 * @param token The JWT string.
 * @param bufferInSeconds An optional buffer in seconds. A positive buffer will make the token be considered unusable
 * earlier. For example, a buffer of 60 means the token is considered unusable if it expires
 * within the next 60 seconds. Defaults to 0.
 * @returns `true` if the token is valid and usable, `false` otherwise.
 */
export function isTokenUsable(token: string, bufferInSeconds = 60): boolean {
	if (!token) {
		return false; // Not usable if no token is provided
	}

	try {
		// Attempt to decode the token.
		const decodedPayload = jwtDecode<{ exp?: number; iat?: number }>(token);

		// Get the current time in seconds since the epoch.
		const nowInSeconds = Math.floor(Date.now() / 1000);

		// 1. Check 'exp' (Expiration Time) claim presence and type:
		// If 'exp' is not present or is not a number, the token's expiration cannot be determined.
		// For safety, consider it unusable.
		if (typeof decodedPayload.exp !== 'number') {
			const message = 'Token does not have a valid "expiration" (exp) claim.';
			if (import.meta.env.DEV) {
				console.warn(message);
			}
			return false;
		}

		// 2. Check if the token is expired, considering the buffer:
		// The token is unusable if the current time plus the buffer is at or after the expiration time.
		// Example:
		//   nowInSeconds: 1700000000
		//   bufferInSeconds: 60
		//   decodedPayload.exp: 1700000050
		//   nowInSeconds + bufferInSeconds (1700000060) >= decodedPayload.exp (1700000050) -> true (token is unusable)
		if (nowInSeconds + bufferInSeconds >= decodedPayload.exp) {
			const message = 'Token has expired or is within the buffer period of expiration.';
			if (import.meta.env.DEV) {
				console.warn(message);
			}
			return false;
		}

		// If all checks pass, the token is considered usable.
		return true;
	} catch (error: unknown) {
		if (import.meta.env.DEV) {
			console.error('isTokenUsable Error:', error);
		}
		return false;
	}
}
